import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IAlertLog, IAlertCounters, ILOV } from '../../shared/models/alert.models'; // Update with actual paths
import { ExternalSystemType, ExternalSystem } from '../../shared/enums/alert.enums';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  private readonly baseUrl = '/api/general';
  private readonly alertCounters$ = new BehaviorSubject<IAlertCounters | null>(null);
  private readonly goToLandingPageSubject = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private toastr: ToastrService) { }

  // Set and Get goToLandingPage from localStorage
  set goToLandingPage(value: boolean) {
    localStorage.setItem('gotolandingpage', JSON.stringify(value));
    this.goToLandingPageSubject.next(value);
  }

  get goToLandingPage(): boolean {
    return JSON.parse(localStorage.getItem('gotolandingpage') || 'false');
  }

  // Convert date string to UTC format
  toUTCDateString(dateString: string): string {
    if (!dateString) return '';
    return dateString.includes('Z') || dateString.includes('+') ? dateString : `${dateString}Z`;

    // Dup start
    // if (!dateString) return '';
    // if (!dateString.includes('Z') && !dateString.includes('+')) {
    //   return `${dateString}Z`;
    // }
    // return dateString;
    // Dup end
  }

  showSuccess(message: string): void {
    this.toastr.success(message, 'Success', {
      timeOut: 3000, // Duration in milliseconds
      closeButton: true,
      progressBar: true,
    });
  }

  // Error handling
  showError(error: HttpErrorResponse): void {
    const message = error.error?.message || 'An unexpected error occurred';
    this.toastr.error(`Error: ${message}`);
    console.error(error);

    // Dup start
    // this.toastr.error(message);
    // Dup end
  }

  showWarning(message: string): void {
    this.toastr.warning(message);
  }

  showErrorWithMessage(message: string, error: HttpErrorResponse): void {
    const errorMsg = error.error?.message || 'An unexpected error occurred';
    this.toastr.error(`${message}. Error: ${errorMsg}`);
    console.error(error);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  // Alerts
  getAlerts(
    showArchived: boolean,
    showInformationalMessages: boolean,
    householdId = 0,
    accountGroupId = 0,
    accountId = 0,
    investorId = 0,
    extended = false,
    getRule = 0
  ): Observable<IAlertLog[]> {
    const url = `${this.baseUrl}/getalerts/${showArchived}/${showInformationalMessages}/${householdId}/${accountGroupId}/${accountId}/${investorId}/${extended}/${getRule}`;
    return this.http.get<IAlertLog[]>(url).pipe(catchError(this.handleError));

    // Dup start
    // const url = `${this.baseUrl}/getalerts/${showArchived}/${showInformationalMessages}/${householdId}/${accountGroupId}/${accountId}/${investorId}/${extended}`;
    // return this.http.get<IAlertLog[]>(url).pipe(catchError(this.handleError));
    // Dup end
  }

  getAlert(alertId: number): Observable<IAlertLog> {
    const url = `${this.baseUrl}/getalert/${alertId}`;
    return this.http.get<IAlertLog>(url).pipe(catchError(this.handleError));
  }

  archiveAlert(alertId: number): Observable<IAlertLog> {
    const url = `${this.baseUrl}/archivealert/${alertId}`;
    return this.http.put<IAlertLog>(url, null).pipe(catchError(this.handleError));

    // Dup start
    // return this.http.put<IAlertLog>(`${this.baseUrl}/archivealert/${alert.id}`, null).pipe(catchError(this.handleError));
    // Dup end
  }

  claimAlert(alertId: number): Observable<IAlertLog> {
    const url = `${this.baseUrl}/claimalert/${alertId}`;
    return this.http.put<IAlertLog>(url, null).pipe(catchError(this.handleError));
  }

  releaseAlert(alertId: number): Observable<IAlertLog> {
    const url = `${this.baseUrl}/releasealert/${alertId}`;
    return this.http.put<IAlertLog>(url, null).pipe(catchError(this.handleError));
  }

  refreshAlertCounters(): void {
    this.http.get<IAlertCounters>(`${this.baseUrl}/getalertscount`).subscribe({
      next: (counters) => {
        localStorage.setItem('alertCounters', JSON.stringify(counters));
        this.alertCounters$.next(counters);
      },
      error: (err) => this.handleError(err),
    });
  }

  getAlertCounters(): IAlertCounters | null {
    return JSON.parse(localStorage.getItem('alertCounters') || 'null');
  }

  // Roles
  getRoles(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/getroles`).pipe(catchError(this.handleError));
  }

  // LOVs
  getLOVs(): Observable<ILOV[]> {
    const cachedLov = localStorage.getItem('LS_LOV');
    const version = localStorage.getItem('LS_VERSION');
    const lovVersion = localStorage.getItem('LS_LOV_VERSION');

    if (cachedLov && lovVersion === version) {
      return of(JSON.parse(cachedLov) as ILOV[]);
    }

    return this.http.get<ILOV[]>(`${this.baseUrl}/getlovs`).pipe(
      map((response) => {
        localStorage.setItem('LS_LOV', JSON.stringify(response));
        localStorage.setItem('LS_LOV_VERSION', version || 'default');
        return response;
      }),
      catchError(this.handleError)
    );
  }

  getLOVsByLovType(lovType: number): Observable<ILOV[]> {
    return this.http.get<ILOV[]>(`${this.baseUrl}/getlovs/${lovType}`).pipe(catchError(this.handleError));
  }

  // External Systems
  getExternalSystems(): Observable<ExternalSystem[]> {
    return this.http.get<ExternalSystem[]>(`${this.baseUrl}/getExternalSystems`).pipe(catchError(this.handleError));
  }

  getExternalSystemsByType(type: ExternalSystemType): Observable<ExternalSystem[]> {
    return this.http.get<ExternalSystem[]>(`${this.baseUrl}/getExternalSystemsByType/${type}`).pipe(catchError(this.handleError));
  }

  getRMDCalculatorEnabled(): Observable<boolean> {
    return this.http.get<boolean>("/api/general/getRMDCalculatorEnabled").pipe(catchError(this.handleError));;
  };

}
